// components/SnDndFormControls.tsx

import React, { useState } from "react";
import SnLabel from "./SnLabel";
import { useDrag, useDrop } from "react-dnd";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SnFindFormContols from "./SnFindFormContols";
import { TpCtrl } from "@shared/type/CommonType";
const ItemType = { Move: "MOVE", Resize: "Resize" };

interface Control {
  tp_ctrl: TpCtrl;
  no_line: string;
  qt_rowindex: number;
  qt_colindex: number;
  qt_rowspan: number;
  qt_colspan: number;
  nm_label?: string;
  cd_param: string;
  tp_format?: string;
  cd_displaycolumn?: string;
  cd_popup?: string;
  cd_clas?: string;
  dc_sql?: string;
}

interface Props {
  isSearchPanel?: boolean;
  isFormPanel?: boolean;
  labletype: string;
  control: Control;
  id: number;
  index: number;
  formData: any;
  requireds: Record<string, boolean>;
  popupCons: Record<string, any>;
  popupRef?: any;
  setRef: (line: string, node: HTMLDivElement | null) => void;
  setInputRef: (el: any, index: number, isReadOnly: boolean) => void;
  handleMoveFocus: (index: number, target: string) => void;
  onTextValueChanged: (param: string, data: string) => void;
  onNumberValueChanged: (param: string, data: number) => void;
  onPeriodValueChanged: (param: string, start: string, end: string) => void;
  onCodeValueChanged: (
    code: string,
    data: string,
    name: string,
    data2: string,
    rowData?: any
  ) => void;
  setReadOnly: (param: string) => boolean;
  onButtonClick: (param: string) => void;
  moveControl?: (fromIdx: number, toIdx: number) => void;
  removeControl?: (idx: number, id: number) => void;
  resizeControl?: (
    Id: number,
    colspan: number,
    rowspan: number,
    x: number,
    y: number
  ) => void;

  filter: Record<string, any>;
  labelSize?: number;
  /** 삭제(X) 버튼을 컨트롤 우상단 대신 라벨(컬럼명) 바로 옆에 붙인다 (AI 메뉴디자이너 미리보기) */
  removeAtLabel?: boolean;
}

const SnDndFormControls: React.FC<Props> = React.memo(
  ({
    isSearchPanel = false,
    isFormPanel = false,
    labletype,
    control,
    id,
    index,
    formData,
    requireds,
    popupCons,
    popupRef,
    setRef,
    setInputRef,
    handleMoveFocus,
    onTextValueChanged,
    onNumberValueChanged,
    onPeriodValueChanged,
    onCodeValueChanged,
    setReadOnly,
    onButtonClick,
    moveControl,
    removeControl,
    resizeControl,
    filter,
    labelSize,
    removeAtLabel = false,
  }) => {
    const [hover, setHover] = useState(false);
    // 라벨(컬럼명)이 실제로 그려지는가 — removeAtLabel 모드에서 X 위치 결정에 쓴다
    const hasLabel = Boolean(
      control.nm_label &&
        control.tp_ctrl !== "SnGroupLabel" &&
        control.tp_ctrl !== "SnLabel" &&
        control.tp_ctrl !== "SnButton"
    );
    const [, drop] = useDrop(
      {
        accept: ItemType.Move,
        canDrop: () => false,
        hover(item: { id: number; index: number }) {
          if (item.id !== id && item.index !== index) {
            moveControl && moveControl(item.index, index);
            item.index = index; // 드래그 중인 아이템의 인덱스를 현재 인덱스로 업데이트
          }
        },
      },
      [moveControl]
    );

    const [{ isDragging }, dragRef] = useDrag(
      () => ({
        type: ItemType.Move,

        item: { id, index },

        collect: (monitor) => ({
          isDragging: monitor.isDragging(),
        }),
      }),
      [id, index, moveControl]
    );

    const [{ isResizing }, resizeRef] = useDrag(
      () => ({
        //resizeRef에서 발생하는 드래그 아이템을 Resize로 정했다.
        type: ItemType.Resize,
        item: {
          id,
          colspan: control && control.qt_colspan,
          rowspan: control && control.qt_rowspan,
        },
        collect: (monitor) => ({
          isResizing: monitor.isDragging(),
        }),
      }),
      [control, resizeControl]
    );

    const [, resizedrop] = useDrop(
      {
        accept: ItemType.Resize,
        canDrop: () => false,
        hover(
          item: { id: number; colspan: number; rowspan: number },
          monitor: any
        ) {
          const { x, y } = monitor.getDifferenceFromInitialOffset();

          resizeControl &&
            resizeControl(item.id, item.colspan, item.rowspan, x, y);
        },
      },
      [resizeControl]
    );

    return (
      <div
        key={control.no_line}
        ref={(node) => {
          setRef(control.no_line, node);
          dragRef(drop(node));
          resizedrop(node);
        }}
        className="flex justify-start"
        style={{
          position: "relative",
          opacity: isDragging ? "0.3" : "1",
          gridColumn: `auto/span ${control.qt_colspan}`,
          gridRow: `auto/span ${control.qt_rowspan}`,
          border: "1px dashed #0b0c69",
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {hasLabel ? (
          <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            <SnLabel
              text={control.nm_label ?? ""}
              type={labletype}
              color={
                Object.hasOwn(requireds, control.cd_param) ? "#0b0c69" : "black"
              }
              size={labelSize}
            />
            {/* 라벨(컬럼명) 옆 삭제 버튼 — removeAtLabel 모드 */}
            {removeAtLabel && !Object.hasOwn(requireds, control.cd_param) && (
              <span
                style={{
                  marginLeft: 2,
                  marginRight: 2,
                  cursor: "pointer",
                  zIndex: 10,
                  display: hover ? "inline-flex" : "none", // hover 시 표시
                  alignItems: "center",
                  justifyContent: "center",
                }}
                title="검색조건 삭제"
                onClick={() => removeControl && removeControl(index, id)}
              >
                <HighlightOffIcon sx={{ fontSize: 14 }} color="error" />
              </span>
            )}
          </div>
        ) : null}

        <div className="w-full" style={{ pointerEvents: "none", opacity: 0.5 }}>
          <SnFindFormContols
            isSearchPanel={isSearchPanel}
            isFormPanel={isFormPanel}
            control={control}
            index={index}
            formData={formData}
            requireds={requireds}
            popupCons={popupCons}
            setRef={setRef}
            setInputRef={setInputRef}
            handleMoveFocus={handleMoveFocus}
            onTextValueChanged={onTextValueChanged}
            onNumberValueChanged={onNumberValueChanged}
            onPeriodValueChanged={onPeriodValueChanged}
            onCodeValueChanged={onCodeValueChanged}
            setReadOnly={setReadOnly}
            onButtonClick={(param: string) => {
              onButtonClick != null && onButtonClick(param);
            }}
            filter={filter}
            popupRef={popupRef}
          />
        </div>
        {control.nm_label && control.tp_ctrl.includes("AfterLabel") ? (
          <SnLabel
            text={control.nm_label}
            type={"AfterLabel"}
            color={
              Object.hasOwn(requireds, control.cd_param) ? "#0b0c69" : "black"
            }
          />
        ) : null}

        {!Object.hasOwn(requireds, control.cd_param) && (
          <>
            {/* 삭제 버튼 — removeAtLabel 모드에서 라벨 옆에 이미 그렸으면 생략 */}
            <span
              style={{
                position: "absolute",
                top: 2,
                right: 10,
                cursor: "pointer",
                zIndex: 10,
                background: "white",
                borderRadius: "50%",
                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                padding: 2,
                display: hover && !(removeAtLabel && hasLabel) ? "flex" : "none", // hover 시 표시
                alignItems: "center",
                justifyContent: "center",
                transition: "opacity 0.2s ease",
              }}
              onClick={() => removeControl && removeControl(index, id)}
            >
              <HighlightOffIcon fontSize="small" color="error" />
            </span>

            {/* 리사이즈 버튼 */}
            <button
              key={control.no_line}
              ref={resizeRef}
              style={{
                position: "absolute",
                right: 2,
                bottom: 2,
                width: 13.5,
                height: 13.5,
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "nwse-resize",
                zIndex: 11,
                display: hover ? "flex" : "none", // hover 시 표시
                alignItems: "flex-end",
                justifyContent: "flex-end",
                pointerEvents: "auto",
              }}
              tabIndex={-1}
              aria-label="Resize"
            >
              <svg width="13.5" height="13.5" viewBox="0 0 18 18">
                <polygon
                  points="18,18 0,18 18,0"
                  fill="#bdbdbd"
                  opacity="0.7"
                />
                <polyline
                  points="12,18 18,12"
                  stroke="#757575"
                  strokeWidth="2"
                  fill="none"
                />
              </svg>
            </button>
          </>
        )}
      </div>
    );
  }
);

export default SnDndFormControls;
